import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { whatsappNumber } from '@goyone/shared';
import { api, qs, upload } from '../lib/api';
import { Icon } from './Icon';
import { MailCompose, type MailLink } from './MailCompose';
import { QuickMessageModal } from './QuickMessage';
import { QuickCallModal } from './QuickCall';
import { useToast } from './Toast';

/**
 * قنوات الوصول إلى شخص، أيقونةً لكلٍّ منها.
 *
 * الغائب يبقى معروضًا باهتًا ومعطّلًا لا يُحذف: غيابه معلومة — من لا بريد
 * له لا تُرسَل إليه حملة — وإخفاء الأيقونة يجعل الصفوف تختلف أعمدتها
 * فيصعب مسحها بالعين.
 *
 * ## وواتساب يفتح داخل النظام دائمًا
 *
 * المحادثة القائمة تُفتح بمعرّفها، والرقم الجديد تُبدأ له محادثةٌ في
 * قنوات ميتا من هناك — لا إحالة إلى `wa.me`: الردّ من النظام يُحفظ في
 * سجل العميل، والردّ من الهاتف يضيع في جهاز من ردّ. وشاشة القنوات هي
 * من يتصرّف حين لا قناة مربوطة — برسالةٍ تشرح لا برابطٍ يهرّب الحديث.
 *
 * ## والبريد كذلك: من صندوق الشركة لا من برنامج الجهاز
 *
 * `mailto:` يفتح برنامج بريد الموظف، فتخرج الرسالة من حسابه الشخصي ولا
 * يعلم بها النظام — فلا تظهر في سجل العميل، ولا يعرف من يفتح ملفّه بعد
 * شهر أن أحدًا راسله. والكتابة هنا تمرّ على صندوق الشركة وتُحفظ مربوطةً
 * بالسجل.
 */
export function ReachIcons({
  name,
  phone,
  email,
  link,
  size = 15,
  onCall,
  waPhone,
  smsPhone,
  candidateId,
  skipRecording,
}: {
  name: string;
  phone: string | null | undefined;
  email: string | null | undefined;
  /** السجل الذي تُنسب إليه الرسالة، فتدخل تاريخه. */
  link?: MailLink;
  size?: number;
  /**
   * يستبدل وجهة زرّ الاتصال.
   *
   * الافتراضي يفتح الرقم في الكونسول، لكن من يقف في الكونسول نفسه
   * (كقائمة المكالمات الأخيرة) يملأ لوحته مباشرةً بدل أن يُبحر إليها.
   */
  onCall?: () => void;
  /** توجيه القنوات: رقمٌ آخر يستقبل واتساب أو SMS (كتوزيع أرقام العميل). */
  waPhone?: string | null;
  smsPhone?: string | null;
  /**
   * مرشّحٌ في التوظيف — لا عميلٌ ولا محتمَل.
   *
   * تُنسب إليه المكالمةُ والمحادثة، وإلّا صارت مكالمةً برقمٍ مجهولٍ في
   * سجلّ المركز ومحادثةً بلا صاحبٍ في صندوق الوارد.
   */
  candidateId?: string | null;
  /** يُعفي المكالمة من سياسة تسجيل ورديات مركز الاتصال. */
  skipRecording?: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const wa = whatsappNumber(waPhone ?? phone);
  const smsNumber = smsPhone ?? phone;
  const [writing, setWriting] = useState(false);
  /* المُرسِل الراسي — واتساب وSMS يفتحانه هنا كما في ملف العميل سواء. */
  const [quick, setQuick] = useState<'WHATSAPP' | 'SMS' | null>(null);
  /* لوحة الاتصال الراسية — الكونسول يحضر إلى الصفحة بدل الإبحار إليه. */
  const [calling, setCalling] = useState(false);

  /*
   * البحث عن المحادثة كسولٌ ومشترك.
   *
   * `enabled` يمنعه حين لا رقم، و`staleTime` طويل لأن الجواب لا يتغيّر
   * أثناء تصفّح صفحة — وبدونه يصير في الجدول خمسة وعشرون طلبًا.
   */
  const found = useQuery({
    queryKey: ['meta', 'by-phone', wa],
    queryFn: () => api<{ conversationId: string | null }>(`/meta/by-phone${qs({ phone: wa })}`),
    enabled: Boolean(wa),
    staleTime: 5 * 60_000,
    /* غياب القنوات ليس خطأً يُعاد: الرابط الخارجي بديلٌ عامل. */
    retry: false,
  });

  /*
   * الإرسال من المُرسِل مباشرةً — منطق ملف العميل نفسه:
   * SMS تُسلَّم لتطبيق الجهاز بنصّها ولا تُعلَّم «أُرسلت» زورًا،
   * وواتساب يجد محادثته بالرقم أو يبدؤها على القناة المربوطة، ثم يرسل
   * النص أو الوسيط — وحالة «فشلت» تُرمى بسببها لا تُبتلع بردّ 200.
   */
  const sendQuick = useMutation({
    mutationFn: async ({ text, file }: { text: string; file: File | null }) => {
      if (quick === 'SMS') {
        if (!smsNumber) throw new Error('لا رقم مسجَّل.');
        window.location.href = `sms:${smsNumber}?body=${encodeURIComponent(text)}`;
        return 'sms-handoff' as const;
      }

      if (!wa) throw new Error('يلزم رقم هاتف بمفتاح دولة لإرسال واتساب.');
      let convId = found.data?.conversationId ?? null;
      if (!convId) {
        const channels = await api<Array<{ id: string; kind: string; isActive: boolean }>>(
          '/meta/channels',
        );
        const ch = channels.find((c) => c.kind === 'WHATSAPP' && c.isActive);
        if (!ch) throw new Error('لا قناة واتساب مربوطة — اربطها من الإعدادات أولًا.');
        const conv = await api<{ id: string }>('/meta/conversations', {
          method: 'POST',
          body: {
            channelId: ch.id,
            phone: wa,
            ...(candidateId
              ? { candidateId }
              : link?.leadId
                ? { leadId: link.leadId }
                : {}),
          },
        });
        convId = conv.id;
      }

      let view: { status: string; error: string | null };
      if (file) {
        const form = new FormData();
        form.append('file', file);
        if (text) form.append('caption', text);
        view = await upload(`/meta/conversations/${convId}/media`, form);
      } else {
        view = await api(`/meta/conversations/${convId}/messages`, {
          method: 'POST',
          body: { body: text },
        });
      }
      if (view.status === 'FAILED') throw new Error(view.error ?? 'فشل الإرسال.');
      return 'sent' as const;
    },
    onSuccess: (kind) => {
      setQuick(null);
      toast(
        kind === 'sms-handoff'
          ? 'فُتح تطبيق الرسائل بنصّك'
          : 'أُرسلت وسُجّلت في محادثة العميل',
      );
      void queryClient.invalidateQueries({ queryKey: ['meta'] });
      void queryClient.invalidateQueries({ queryKey: ['timeline'] });
    },
  });

  return (
    <div className="channels">
      {/*
        الاتصال في مكانه: لوحة الكونسول تنفتح راسيةً هنا — تبدأ المكالمة
        وتُسجَّل وتُنهى بنتيجتها بلا مغادرة الصفحة. ومن يقف في الكونسول
        نفسه يمرّر `onCall` فيملأ لوحته مباشرة.
      */}
      <button
        type="button"
        className={`channel${phone ? '' : ' channel-off'}`}
        onClick={() => phone && (onCall ? onCall() : setCalling(true))}
        disabled={!phone}
        title={phone ? `اتصال — ${phone}` : 'لا رقم مسجَّل'}
        aria-label={phone ? `اتصال بـ${name}` : 'لا رقم مسجَّل'}
      >
        <Icon name="phone" size={size} />
      </button>

      {/*
        SMS تُكتب في المُرسِل الراسي بإيموجيها ومحفوظاتها ثم تُسلَّم
        لتطبيق الرسائل في الجهاز — لا بوابة SMS في النظام بعد، فلا
        تُسجَّل في سجل العميل، والتلميح يقولها بلا تجميل.
      */}
      <button
        type="button"
        className={`channel${smsNumber ? '' : ' channel-off'}`}
        onClick={() => smsNumber && setQuick('SMS')}
        disabled={!smsNumber}
        title={
          smsNumber ? `رسالة SMS — تُكتب هنا وتُرسَل من جهازك إلى ${smsNumber}` : 'لا رقم مسجَّل'
        }
        aria-label={smsNumber ? `رسالة SMS إلى ${name}` : 'لا رقم مسجَّل'}
      >
        <Icon name="sms" size={size} />
      </button>

      {/* واتساب يفتح المُرسِل بثيمته ويُرسل مباشرةً — كملف العميل سواء. */}
      <button
        type="button"
        className={`channel${wa ? '' : ' channel-off'}${found.data?.conversationId ? ' channel-live' : ''}`}
        onClick={() => wa && setQuick('WHATSAPP')}
        disabled={!wa}
        title={
          !wa
            ? 'يلزم رقم بمفتاح دولة'
            : found.data?.conversationId
              ? 'رسالة واتساب — تُسجَّل في محادثته القائمة'
              : 'رسالة واتساب — تُرسَل مباشرة'
        }
        aria-label={wa ? `واتساب ${name}` : 'واتساب غير متاح'}
      >
        <Icon name="chat" size={size} />
      </button>

      <button
        type="button"
        className={`channel${email ? '' : ' channel-off'}`}
        onClick={() => setWriting(true)}
        disabled={!email}
        title={email ? `راسل ${email} من صندوق الشركة` : 'لا بريد مسجَّل'}
        aria-label={email ? `مراسلة ${name}` : 'لا بريد مسجَّل'}
      >
        <Icon name="mail" size={size} />
      </button>

      {writing && email && (
        <MailCompose to={email} link={link} onClose={() => setWriting(false)} />
      )}

      {calling && phone && (
        <QuickCallModal
          clientName={name}
          phone={phone}
          link={{
            leadId: link?.leadId,
            accountId: link?.accountRefId,
            contactId: link?.contactId,
            candidateId,
          }}
          skipRecording={skipRecording}
          onClose={() => setCalling(false)}
        />
      )}

      {quick && (
        <QuickMessageModal
          channel={quick}
          clientName={name}
          hint={
            quick === 'SMS'
              ? 'تُكتب هنا وتُسلَّم لتطبيق الرسائل في جهازك بنصّها — لا تُسجَّل في النظام.'
              : found.data?.conversationId
                ? 'تُرسَل عبر قناة واتساب المربوطة وتُسجَّل في محادثة العميل — والوسائط كذلك.'
                : 'تُرسَل عبر قناة واتساب المربوطة وتُبدأ بها محادثة تُسجَّل في النظام.'
          }
          pending={sendQuick.isPending}
          error={sendQuick.error instanceof Error ? sendQuick.error.message : null}
          onSend={(text, file) => sendQuick.mutate({ text, file })}
          onClose={() => setQuick(null)}
        />
      )}
    </div>
  );
}
